import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../supabase';
import SchoolSidebar from './SchoolSidebar';
import { SchoolMobileHeader } from './SchoolMobileHeader';
import BranchSelector from './BranchSelector';
import { useBranchFilter } from '../../hooks/useBranchFilter';
import './SchoolDashboard.css';

const Receptionists = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [schoolData, setSchoolData] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [receptionists, setReceptionists] = useState([]);
  const [loadingReceptionists, setLoadingReceptionists] = useState(true);
  const [error, setError] = useState(null);
  const [editingReceptionist, setEditingReceptionist] = useState(null);
  const [viewingReceptionist, setViewingReceptionist] = useState(null);
  const [viewMode, setViewMode] = useState('active'); // 'active' or 'archived'
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showArchiveConfirmPopup, setShowArchiveConfirmPopup] = useState(false);
  const [receptionistToArchive, setReceptionistToArchive] = useState(null);
  const [showArchiveSuccessPopup, setShowArchiveSuccessPopup] = useState(false);
  const [archivedReceptionistName, setArchivedReceptionistName] = useState('');
  const [showRestoreConfirmPopup, setShowRestoreConfirmPopup] = useState(false);
  const [receptionistToRestore, setReceptionistToRestore] = useState(null);
  const [showRestoreSuccessPopup, setShowRestoreSuccessPopup] = useState(false);
  const [restoredReceptionistName, setRestoredReceptionistName] = useState('');
  const [showDeleteConfirmPopup, setShowDeleteConfirmPopup] = useState(false);
  const [receptionistToDelete, setReceptionistToDelete] = useState(null);
  const [showDeleteSuccessPopup, setShowDeleteSuccessPopup] = useState(false);
  const [deletedReceptionistName, setDeletedReceptionistName] = useState('');
  const [editForm, setEditForm] = useState({
    name: '',
    email: '',
    employee_id: '',
    phone: '',
    date_of_birth: '',
    gender: '',
    nationality: '',
    address: '',
    password: '',
    confirmPassword: '',
    branch_id: null
  });
  const [editErrors, setEditErrors] = useState({});
  const { selectedBranchId, handleBranchChange } = useBranchFilter();
  
  // Branch state
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(false);

  useEffect(() => {
    // Get school data from localStorage
    const storedSchoolData = localStorage.getItem('schoolData');
    if (storedSchoolData) {
      const parsedData = JSON.parse(storedSchoolData);
      setSchoolData(parsedData);
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    if (schoolData?.id) {
      fetchReceptionists();
    }
  }, [schoolData?.id, viewMode, selectedBranchId]);

  // Fetch branches when school data is available
  useEffect(() => {
    const fetchBranches = async () => {
      if (!schoolData?.id) return;
      
      try {
        setLoadingBranches(true);
        const { data, error } = await supabase
          .from('branches')
          .select('*')
          .eq('school_id', schoolData.id)
          .eq('is_active', true)
          .order('branch_name');
        
        if (error) {
          console.error('Error fetching branches:', error);
          setBranches([]);
        } else {
          setBranches(data || []);
        }
      } catch (err) {
        console.error('Error:', err);
        setBranches([]);
      } finally {
        setLoadingBranches(false);
      }
    };

    if (schoolData?.id) {
      fetchBranches();
    }
  }, [schoolData?.id]);

  const fetchReceptionists = async () => {
    if (!schoolData?.id) return;
    
    try {
      setLoadingReceptionists(true);
      let query = supabase
        .from('receptionists')
        .select('*')
        .eq('school_id', schoolData.id);
      
      if (selectedBranchId) {
        query = query.eq('branch_id', selectedBranchId);
      }
      
      // Filter by active/inactive status based on view mode
      if (viewMode === 'active') {
        query = query.eq('is_active', true);
      } else {
        query = query.eq('is_active', false);
      }
      
      const { data, error } = await query.order('name');
      
      if (error) {
        console.error('Error fetching receptionists:', error);
        setError('Failed to load receptionists');
        return;
      }
      
      setReceptionists(data || []);
      setError(null);
    } catch (err) {
      console.error('Error:', err);
      setError('Failed to load receptionists');
    } finally {
      setLoadingReceptionists(false);
    }
  };


  const handleArchiveReceptionist = (receptionist) => {
    setReceptionistToArchive(receptionist);
    setShowArchiveConfirmPopup(true);
  };

  const confirmArchiveReceptionist = async () => {
    if (!receptionistToArchive) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('receptionists')
        .update({ 
          is_active: false,
          updated_at: new Date().toISOString()
        })
        .eq('id', receptionistToArchive.id);
      
      if (error) {
        console.error('Error archiving receptionist:', error);
        alert('Failed to archive receptionist. Please try again.');
        setIsSubmitting(false);
        setShowArchiveConfirmPopup(false);
        setReceptionistToArchive(null);
        return;
      }

      // Store archived receptionist name for success popup
      const archivedName = receptionistToArchive.name;
      
      // Refresh the list
      await fetchReceptionists();
      setShowArchiveConfirmPopup(false);
      setReceptionistToArchive(null);
      
      // Show success popup
      setArchivedReceptionistName(archivedName);
      setShowArchiveSuccessPopup(true);
    } catch (err) {
      console.error('Error archiving receptionist:', err);
      alert('Failed to archive receptionist. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelArchiveReceptionist = () => {
    setShowArchiveConfirmPopup(false);
    setReceptionistToArchive(null);
  };

  const handleRestoreReceptionist = (receptionist) => {
    setReceptionistToRestore(receptionist);
    setShowRestoreConfirmPopup(true);
  };

  const confirmRestoreReceptionist = async () => {
    if (!receptionistToRestore) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('receptionists')
        .update({ 
          is_active: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', receptionistToRestore.id);
      
      if (error) {
        console.error('Error restoring receptionist:', error);
        alert('Failed to restore receptionist. Please try again.');
        setIsSubmitting(false);
        setShowRestoreConfirmPopup(false);
        setReceptionistToRestore(null);
        return;
      }

      // Store restored receptionist name for success popup
      const restoredName = receptionistToRestore.name;
      
      // Refresh the list
      await fetchReceptionists();
      setShowRestoreConfirmPopup(false);
      setReceptionistToRestore(null);
      
      // Show success popup
      setRestoredReceptionistName(restoredName);
      setShowRestoreSuccessPopup(true);
    } catch (err) {
      console.error('Error restoring receptionist:', err);
      alert('Failed to restore receptionist. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelRestoreReceptionist = () => {
    setShowRestoreConfirmPopup(false);
    setReceptionistToRestore(null);
  };

  const handleDeleteReceptionist = (receptionistId, receptionistName) => {
    setReceptionistToDelete({
      id: receptionistId,
      name: receptionistName
    });
    setShowDeleteConfirmPopup(true);
  };

  const confirmDeleteReceptionist = async () => {
    if (!receptionistToDelete) return;

    setIsSubmitting(true);
    try {
      const { error } = await supabase
        .from('receptionists')
        .delete()
        .eq('id', receptionistToDelete.id);

      if (error) {
        console.error('Error deleting receptionist:', error);
        alert('Failed to delete receptionist. Please try again.');
        setIsSubmitting(false);
        setShowDeleteConfirmPopup(false);
        setReceptionistToDelete(null);
        return;
      }

      // Store deleted receptionist name for success popup
      const deletedName = receptionistToDelete.name;

      // Refresh the list
      await fetchReceptionists();
      
      // Close confirmation popup and show success popup
      setShowDeleteConfirmPopup(false);
      setReceptionistToDelete(null);
      setDeletedReceptionistName(deletedName);
      setShowDeleteSuccessPopup(true);
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to delete receptionist. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const cancelDeleteReceptionist = () => {
    setShowDeleteConfirmPopup(false);
    setReceptionistToDelete(null);
  };

  const handleViewReceptionist = (receptionist) => {
    setViewingReceptionist(receptionist);
  };

  const closeViewPopup = () => {
    setViewingReceptionist(null);
  };

  const handleEditReceptionist = (receptionist) => {
    setEditingReceptionist(receptionist);
    setEditForm({
      name: receptionist.name || '',
      email: receptionist.email || '',
      employee_id: receptionist.employee_id || '',
      phone: receptionist.phone || '',
      date_of_birth: receptionist.date_of_birth || '',
      gender: receptionist.gender || '',
      nationality: receptionist.nationality || 'Kenyan',
      address: receptionist.address || '',
      branch_id: receptionist.branch_id || null,
      password: '',
      confirmPassword: ''
    });
    setEditErrors({});
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    
    // Handle branch_id - convert to number or null
    let processedValue = value;
    if (name === 'branch_id') {
      processedValue = value === '' ? null : parseInt(value, 10);
    }
    
    setEditForm(prev => ({
      ...prev,
      [name]: processedValue
    }));
    
    // Clear error for this field
    if (editErrors[name]) {
      setEditErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateEditForm = () => {
    const errors = {};
    
    if (!editForm.name.trim()) errors.name = 'Name is required';
    if (!editForm.email.trim()) errors.email = 'Email is required';
    else if (!/\S+@\S+\.\S+/.test(editForm.email)) errors.email = 'Email is invalid';
    
    // Password validation (only if passwords are provided)
    // No length restriction
    
    if (editForm.password && editForm.password !== editForm.confirmPassword) {
      errors.confirmPassword = 'Passwords do not match';
    }
    
    setEditErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateEditForm()) return;
    
    setIsSubmitting(true);
    
    try {
      // Update receptionist details
      const { error: updateError } = await supabase
        .from('receptionists')
        .update({
          name: editForm.name.trim(),
          email: editForm.email.trim(),
          employee_id: editForm.employee_id.trim() || null,
          phone: editForm.phone.trim() || null,
          date_of_birth: editForm.date_of_birth || null,
          gender: editForm.gender || null,
          nationality: editForm.nationality || null,
          address: editForm.address?.trim() || null,
          branch_id: editForm.branch_id || null,
          updated_at: new Date().toISOString()
        })
        .eq('id', editingReceptionist.id);
      
      if (updateError) {
        console.error('Error updating receptionist:', updateError);
        alert(`Failed to update receptionist: ${updateError.message || 'Please check your data and try again.'}`);
        return;
      }
      
      // Update password if provided
      if (editForm.password && editForm.password !== '') {
        try {
          // Find the receptionist's auth user ID
          const { data: userData, error: authError } = await supabase
            .from('users')
            .select('id')
            .eq('email', editingReceptionist.email)
            .eq('user_type', 'receptionist')
            .single();
          
          if (authError || !userData) {
            console.warn('Receptionist auth user not found for password update:', authError);
          } else {
            // Update password using Supabase Auth Admin API
            const { error: passwordError } = await supabase.auth.admin.updateUserById(
              userData.id,
              { password: editForm.password }
            );
            
            if (passwordError) {
              console.warn('Failed to update receptionist password:', passwordError);
            } else {
              console.log('Receptionist password updated successfully');
            }
          }
        } catch (passwordErr) {
          console.warn('Error updating receptionist password:', passwordErr);
        }
      }
      
      // Refresh receptionists list
      await fetchReceptionists();
      
      // Close popup
      setEditingReceptionist(null);
      setEditForm({
        name: '',
        email: '',
        employee_id: '',
        phone: '',
        date_of_birth: '',
        gender: '',
        nationality: '',
        address: '',
        branch_id: null,
        password: '',
        confirmPassword: ''
      });
      setEditErrors({});
      
      alert('Receptionist updated successfully!');
      
    } catch (err) {
      console.error('Error:', err);
      alert('Failed to update receptionist. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const closeEditPopup = () => {
    setEditingReceptionist(null);
    setEditForm({
      name: '',
      email: '',
      employee_id: '',
      phone: '',
      date_of_birth: '',
      gender: '',
      nationality: '',
      address: '',
      branch_id: null,
      password: '',
      confirmPassword: ''
    });
    setEditErrors({});
  };

  const handleLogout = () => {
    navigate('/schoollogin');
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">⏳</div>
        <p>Loading...</p>
      </div>
    );
  }

  if (!schoolData) {
    return (
      <div className="loading-container">
        <div className="loading-spinner">❌</div>
        <p>School data not found. Please login again.</p>
        <button onClick={() => navigate('/schoollogin')} className="submit-btn">
          Go to Login
        </button>
      </div>
    );
  }

  return (
    <div className="school-dashboard">
      <SchoolMobileHeader 
        schoolData={schoolData} 
        mobileMenuOpen={mobileMenuOpen} 
        setMobileMenuOpen={setMobileMenuOpen} 
        handleLogout={handleLogout} 
      />
      
      {/* Sidebar */}
      <SchoolSidebar schoolData={schoolData} onLogout={handleLogout} />

      {/* Main Content */}
      <div className="main-content">
        <div className="students-content">
          <div className="students-header">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '1rem' }}>
              <div>
                <h1>Receptionists Management</h1>
                <p>{viewMode === 'active' ? 'View and manage all active receptionists in your school' : 'View and manage archived receptionists'}</p>
              </div>
              <BranchSelector
                schoolId={schoolData?.id}
                selectedBranchId={selectedBranchId}
                onBranchChange={handleBranchChange}
              />
            </div>
          </div>

          <div className="students-stats">
            <div className="stat-item">
              <span className="stat-number">{receptionists.length}</span>
              <span className="stat-label">Total Receptionists</span>
            </div>
          </div>

          <div className="students-actions">
            {/* Add Receptionist Button */}
            <div style={{ marginBottom: '1rem', display: 'flex', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => navigate('/school/add-receptionist')}
                className="submit-btn"
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '0.5rem'
                }}
              >
                <span>➕</span>
                <span>Add Receptionist</span>
              </button>
            </div>
            {/* View Mode Toggle */}
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '0.5rem', background: '#F3F4F6', borderRadius: '8px', padding: '0.25rem', width: 'fit-content', marginBottom: '1.5rem' }}>
              <button
                onClick={() => setViewMode('active')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'active' ? '#FFFFFF' : 'transparent',
                  color: viewMode === 'active' ? '#1F2937' : '#6B7280',
                  fontWeight: viewMode === 'active' ? '600' : '400',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'active' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '1rem'
                }}
              >
                <span style={{ 
                  fontSize: '1em', 
                  width: '1em', 
                  height: '1em', 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  lineHeight: '1'
                }}>👥</span>
                Active Receptionists
              </button>
              <button
                onClick={() => setViewMode('archived')}
                style={{
                  padding: '0.5rem 1rem',
                  borderRadius: '6px',
                  border: 'none',
                  background: viewMode === 'archived' ? '#FFFFFF' : 'transparent',
                  color: viewMode === 'archived' ? '#1F2937' : '#6B7280',
                  fontWeight: viewMode === 'archived' ? '600' : '400',
                  cursor: 'pointer',
                  boxShadow: viewMode === 'archived' ? '0 2px 4px rgba(0,0,0,0.1)' : 'none',
                  transition: 'all 0.2s',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  fontSize: '1rem'
                }}
              >
                <span style={{ 
                  fontSize: '1em', 
                  width: '1em', 
                  height: '1em', 
                  display: 'inline-flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  lineHeight: '1'
                }}>📦</span>
                Archived Receptionists
              </button>
            </div>
          </div>

          {loadingReceptionists ? (
            <div className="loading-container">
              <div className="loading-spinner">⏳</div>
              <p>Loading receptionists...</p>
            </div>
          ) : error ? (
            <div className="error-container">
              <div className="error-icon">❌</div>
              <p>{error}</p>
              <button 
                className="retry-btn"
                onClick={() => window.location.reload()}
              >
                Retry
              </button>
            </div>
          ) : receptionists.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">📞</div>
              <h3>No {viewMode === 'active' ? 'Active' : 'Archived'} Receptionists Found</h3>
              <p>
                {viewMode === 'active' 
                  ? "You haven't added any active receptionists yet. Start by adding your first receptionist!"
                  : "You don't have any archived receptionists yet."}
              </p>
              {viewMode === 'active' && (
                <button 
                  className="add-first-teacher-btn"
                  onClick={() => navigate('/school/add-receptionist')}
                >
                  ➕ Add Your First Receptionist
                </button>
              )}
            </div>
          ) : (
            <div className="students-table-container">
              <table className="students-table" style={{ tableLayout: 'fixed' }}>
                <thead>
                  <tr>
                    <th style={{ width: '25%' }}>Receptionist Name</th>
                    <th style={{ width: '35%' }}>Email</th>
                    <th style={{ width: '15%' }}>Created Date</th>
                    <th style={{ width: '25%' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {receptionists.map((receptionist) => (
                    <tr key={receptionist.id}>
                      <td className="teacher-name" style={{ color: '#000000' }}>
                        <strong>{receptionist.name}</strong>
                      </td>
                      <td className="email-cell">
                        <a href={`mailto:${receptionist.email}`} className="email-link">
                          {receptionist.email}
                        </a>
                      </td>
                      <td className="created-date">
                        {new Date(receptionist.created_at).toLocaleDateString()}
                      </td>
                      <td className="actions-cell">
                        <div className="action-buttons">
                          {viewMode === 'active' ? (
                            <>
                              <button 
                                className="view-btn"
                                title="View Details"
                                onClick={() => handleViewReceptionist(receptionist)}
                                style={{
                                  background: 'transparent',
                                  color: '#FF8C42',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '0.4rem 0.6rem',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  marginRight: '0.25rem'
                                }}
                              >
                                👁️
                              </button>
                              <button 
                                className="edit-btn"
                                title="Edit Receptionist"
                                onClick={() => handleEditReceptionist(receptionist)}
                                style={{
                                  background: 'transparent',
                                  color: '#FF8C42',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '0.4rem 0.6rem',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  marginRight: '0.25rem'
                                }}
                              >
                                ✏️
                              </button>
                              <button 
                                className="archive-btn"
                                title="Archive Receptionist"
                                onClick={() => handleArchiveReceptionist(receptionist)}
                                disabled={isSubmitting}
                                style={{
                                  background: 'transparent',
                                  color: '#8B5CF6',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '0.4rem 0.6rem',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  marginRight: '0.25rem',
                                  opacity: isSubmitting ? 0.5 : 1
                                }}
                              >
                                📦
                              </button>
                              <button 
                                className="delete-btn"
                                title="Delete Receptionist"
                                onClick={() => handleDeleteReceptionist(receptionist.id, receptionist.name)}
                                disabled={isSubmitting}
                                style={{
                                  background: 'transparent',
                                  color: '#FF8C42',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '0.4rem 0.6rem',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  opacity: isSubmitting ? 0.5 : 1
                                }}
                              >
                                🗑️
                              </button>
                            </>
                          ) : (
                            <>
                              <button 
                                className="unarchive-btn"
                                title="Restore Receptionist"
                                onClick={() => handleRestoreReceptionist(receptionist)}
                                disabled={isSubmitting}
                                style={{
                                  background: 'transparent',
                                  color: '#10B981',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '0.4rem 0.6rem',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  marginRight: '0.25rem',
                                  opacity: isSubmitting ? 0.5 : 1
                                }}
                              >
                                ♻️
                              </button>
                              <button 
                                className="delete-btn"
                                title="Delete Receptionist"
                                onClick={() => handleDeleteReceptionist(receptionist.id, receptionist.name)}
                                disabled={isSubmitting}
                                style={{
                                  background: 'transparent',
                                  color: '#FF8C42',
                                  border: 'none',
                                  borderRadius: '4px',
                                  padding: '0.4rem 0.6rem',
                                  cursor: 'pointer',
                                  fontSize: '1.2rem',
                                  opacity: isSubmitting ? 0.5 : 1
                                }}
                              >
                                🗑️
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* View Receptionist Details Popup */}
      {viewingReceptionist && (
        <div className="popup-overlay" onClick={closeViewPopup}>
          <div className="popup-content" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px', maxHeight: '90vh', overflowY: 'auto', paddingBottom: '2rem' }}>
            <div className="popup-header" style={{ backgroundColor: '#FF8C42', borderRadius: '8px 8px 0 0', padding: '1rem 1.5rem' }}>
              <h3 style={{ color: '#FFFFFF', margin: 0, fontSize: '1.25rem', fontWeight: '600' }}>
                Receptionist Details: {viewingReceptionist.name}
              </h3>
              <button className="close-btn" onClick={closeViewPopup} style={{ color: '#FFFFFF', fontSize: '1.5rem', background: 'none', border: 'none', cursor: 'pointer', padding: '0', width: '30px', height: '30px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                ×
              </button>
            </div>
            
            <div style={{ padding: '1.5rem', backgroundColor: '#F9FAFB' }}>
              {/* Receptionist Information Card */}
              <div className="detail-section" style={{ marginBottom: '2rem' }}>
                <h4 style={{ color: '#FF8C42', marginBottom: '1.5rem', fontSize: '1.2rem', borderBottom: '2px solid #FF8C42', paddingBottom: '0.5rem' }}>
                  Receptionist Information
                </h4>
                <div style={{
                  padding: '1.5rem',
                  background: '#FFFFFF',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  boxShadow: '0 2px 4px rgba(0, 0, 0, 0.05)',
                  position: 'relative',
                  overflow: 'hidden'
                }}>
                  <div style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '4px',
                    background: 'linear-gradient(90deg, #FF8C42 0%, #FFA366 100%)'
                  }}></div>
                  
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginTop: '0.5rem' }}>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>First Name</span>
                      <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                        {viewingReceptionist.first_name || viewingReceptionist.name?.split(' ')[0] || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Last Name</span>
                      <div style={{ color: '#1F2937', fontSize: '1rem', fontWeight: '600', marginTop: '0.25rem' }}>
                        {viewingReceptionist.last_name || viewingReceptionist.name?.split(' ').slice(1).join(' ') || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Employee ID</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingReceptionist.employee_id || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Branch</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingReceptionist.branch_id ? (
                          (() => {
                            const branch = branches.find(b => b.id === viewingReceptionist.branch_id);
                            return branch ? branch.branch_name : 'N/A';
                          })()
                        ) : 'All Branches'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Email</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingReceptionist.email ? (
                          <a href={`mailto:${viewingReceptionist.email}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                            {viewingReceptionist.email}
                          </a>
                        ) : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Phone Number</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingReceptionist.phone ? (
                          <a href={`tel:${viewingReceptionist.phone}`} style={{ color: '#1F2937', textDecoration: 'none' }} onMouseEnter={(e) => e.target.style.textDecoration = 'underline'} onMouseLeave={(e) => e.target.style.textDecoration = 'none'}>
                            {viewingReceptionist.phone}
                          </a>
                        ) : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Date of Birth</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingReceptionist.date_of_birth ? new Date(viewingReceptionist.date_of_birth).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Gender</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingReceptionist.gender || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Nationality</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingReceptionist.nationality || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Status</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        <span style={{
                          padding: '0.25rem 0.75rem',
                          borderRadius: '12px',
                          fontSize: '0.85rem',
                          fontWeight: '500',
                          background: viewingReceptionist.is_active ? '#D1FAE5' : '#FEE2E2',
                          color: viewingReceptionist.is_active ? '#065F46' : '#991B1B'
                        }}>
                          {viewingReceptionist.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                    </div>
                    <div style={{ gridColumn: '1 / -1' }}>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Address</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingReceptionist.address || 'N/A'}
                      </div>
                    </div>
                    <div>
                      <span style={{ color: '#6B7280', fontSize: '0.85rem', fontWeight: '500' }}>Created Date</span>
                      <div style={{ color: '#1F2937', fontSize: '0.95rem', marginTop: '0.25rem' }}>
                        {viewingReceptionist.created_at ? new Date(viewingReceptionist.created_at).toLocaleDateString() : 'N/A'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Receptionist Popup */}
      {editingReceptionist && (
        <div className="popup-overlay">
          <div className="popup-content">
            <div className="popup-header">
              <h2>Edit Receptionist</h2>
              <button className="close-btn" onClick={closeEditPopup}>×</button>
            </div>
            
            <form onSubmit={handleEditSubmit} className="edit-teacher-form">
              <div className="form-section">
                <h3>Receptionist Information</h3>
                {/* Branch Selection */}
                {branches.length > 0 && (
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="branch_id">Branch</label>
                      <select
                        id="branch_id"
                        name="branch_id"
                        value={editForm.branch_id || ''}
                        onChange={handleEditInputChange}
                        className={editErrors.branch_id ? 'error' : ''}
                      >
                        <option value="">-- Select Branch (Optional) --</option>
                        {branches.map((branch) => (
                          <option key={branch.id} value={branch.id}>
                            {branch.branch_name}
                          </option>
                        ))}
                      </select>
                      {editErrors.branch_id && (
                        <span className="error-text">{editErrors.branch_id}</span>
                      )}
                      <small className="form-help">Select the branch this receptionist is assigned to. Leave empty if assigned to all branches.</small>
                    </div>
                  </div>
                )}
                <div className="form-row">
                  <div className="form-group">
                    <label>Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={editForm.name}
                      onChange={handleEditInputChange}
                      className={editErrors.name ? 'error' : ''}
                    />
                    {editErrors.name && <span className="error-text">{editErrors.name}</span>}
                  </div>
                  <div className="form-group">
                    <label>Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={editForm.email}
                      onChange={handleEditInputChange}
                      className={editErrors.email ? 'error' : ''}
                    />
                    {editErrors.email && <span className="error-text">{editErrors.email}</span>}
                  </div>
                  <div className="form-group">
                    <label>Employee ID</label>
                    <input
                      type="text"
                      name="employee_id"
                      value={editForm.employee_id}
                      onChange={handleEditInputChange}
                      placeholder="Enter employee ID (optional)"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Phone Number</label>
                    <input
                      type="text"
                      name="phone"
                      value={editForm.phone}
                      onChange={handleEditInputChange}
                      placeholder="Enter phone number"
                    />
                  </div>
                  <div className="form-group">
                    <label>Date of Birth</label>
                    <input
                      type="date"
                      name="date_of_birth"
                      value={editForm.date_of_birth}
                      onChange={handleEditInputChange}
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label>Gender</label>
                    <select
                      name="gender"
                      value={editForm.gender}
                      onChange={handleEditInputChange}
                    >
                      <option value="">-- Select Gender --</option>
                      <option value="Male">Male</option>
                      <option value="Female">Female</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Nationality</label>
                    <input
                      type="text"
                      name="nationality"
                      value={editForm.nationality}
                      onChange={handleEditInputChange}
                      placeholder="Enter nationality"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group" style={{ width: '100%' }}>
                    <label>Address</label>
                    <textarea
                      name="address"
                      value={editForm.address}
                      onChange={handleEditInputChange}
                      rows="3"
                      placeholder="Enter full address"
                    />
                  </div>
                </div>
              </div>

              <div className="form-section">
                <h3>Password Change (Optional)</h3>
                <div className="password-change-info">
                  <p>Leave password fields empty if you don't want to change the password.</p>
                </div>
                
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="editPassword">New Password</label>
                    <input
                      type="password"
                      id="editPassword"
                      name="password"
                      value={editForm.password}
                      onChange={handleEditInputChange}
                      className={editErrors.password ? 'error' : ''}
                      placeholder="Enter new password"
                    />
                    {editErrors.password && (
                      <span className="error-text">{editErrors.password}</span>
                    )}
                  </div>
                  
                  <div className="form-group">
                    <label htmlFor="editConfirmPassword">Confirm Password</label>
                    <input
                      type="password"
                      id="editConfirmPassword"
                      name="confirmPassword"
                      value={editForm.confirmPassword}
                      onChange={handleEditInputChange}
                      className={editErrors.confirmPassword ? 'error' : ''}
                      placeholder="Confirm new password"
                    />
                    {editErrors.confirmPassword && (
                      <span className="error-text">{editErrors.confirmPassword}</span>
                    )}
                  </div>
                </div>
              </div>

              <div className="form-actions">
                <button type="button" className="cancel-btn" onClick={closeEditPopup}>
                  Cancel
                </button>
                <button type="submit" className="submit-btn" disabled={isSubmitting}>
                  {isSubmitting ? 'Updating...' : 'Update Receptionist'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Archive Confirmation Popup */}
      {showArchiveConfirmPopup && receptionistToArchive && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelArchiveReceptionist}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(139, 92, 246, 0.1) 0%, rgba(124, 58, 237, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(139, 92, 246, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>📦</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Archive Receptionist?
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '1.5rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              Are you sure you want to archive <strong>{receptionistToArchive.name}</strong>?
              <br /><br />
              The receptionist will be marked as inactive and hidden from the active receptionists list. They can be restored later if needed.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
              <button onClick={cancelArchiveReceptionist} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: '2px solid #E5E7EB', background: '#FFFFFF', color: '#6B7280', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1 }}>
                Cancel
              </button>
              <button onClick={confirmArchiveReceptionist} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1, boxShadow: '0 4px 12px rgba(139, 92, 246, 0.4)' }}>
                {isSubmitting ? 'Archiving...' : 'Archive Receptionist'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Archive Success Popup */}
      {showArchiveSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowArchiveSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>✓</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Receptionist Archived Successfully! 🎉
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '2rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              <strong>{archivedReceptionistName}</strong> has been archived successfully.
              <br />
              They are now marked as inactive.
            </p>
            <button onClick={() => setShowArchiveSuccessPopup(false)} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)' }}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Restore Confirmation Popup */}
      {showRestoreConfirmPopup && receptionistToRestore && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelRestoreReceptionist}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>♻️</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Restore Receptionist?
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '1.5rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              Are you sure you want to restore <strong>{receptionistToRestore.name}</strong>?
              <br /><br />
              The receptionist will be marked as active and will appear in the active receptionists list.
            </p>
            <div style={{ display: 'flex', gap: '1rem', justifyContent: 'center', position: 'relative', zIndex: 1 }}>
              <button onClick={cancelRestoreReceptionist} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: '2px solid #E5E7EB', background: '#FFFFFF', color: '#6B7280', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1 }}>
                Cancel
              </button>
              <button onClick={confirmRestoreReceptionist} disabled={isSubmitting} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: isSubmitting ? 'not-allowed' : 'pointer', transition: 'all 0.3s ease', opacity: isSubmitting ? 0.5 : 1, boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)' }}>
                {isSubmitting ? 'Restoring...' : 'Restore Receptionist'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Restore Success Popup */}
      {showRestoreSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowRestoreSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ fontSize: '4rem', color: '#FFFFFF', fontWeight: 'bold', lineHeight: 1 }}>✓</span>
            </div>
            <h2 style={{ color: '#1F2937', fontSize: '2rem', fontWeight: '700', marginBottom: '0.5rem', position: 'relative', zIndex: 1 }}>
              Receptionist Restored Successfully! 🎉
            </h2>
            <p style={{ color: '#6B7280', fontSize: '1rem', marginBottom: '2rem', lineHeight: '1.6', position: 'relative', zIndex: 1 }}>
              <strong>{restoredReceptionistName}</strong> has been restored successfully.
              <br />
              The receptionist is now marked as active.
            </p>
            <button onClick={() => setShowRestoreSuccessPopup(false)} style={{ padding: '0.75rem 2rem', borderRadius: '12px', border: 'none', background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)', color: '#FFFFFF', fontSize: '1rem', fontWeight: '600', cursor: 'pointer', transition: 'all 0.3s ease', boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)' }}>
              OK
            </button>
          </div>
        </div>
      )}

      {/* Delete Confirmation Popup */}
      {showDeleteConfirmPopup && receptionistToDelete && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={cancelDeleteReceptionist}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(239, 68, 68, 0.1) 0%, rgba(220, 38, 38, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Delete Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(239, 68, 68, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>🗑️</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Delete Receptionist?
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '1.5rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              Are you sure you want to <strong style={{ color: '#EF4444' }}>PERMANENTLY DELETE</strong> <strong>{receptionistToDelete.name}</strong>?
              <br /><br />
              This will:
              <br />
              • Remove the receptionist from the database completely
              <br />
              • Delete their login account (if exists)
              <br />
              <strong style={{ color: '#EF4444' }}>This action CANNOT be undone!</strong>
            </p>

            {/* Buttons */}
            <div style={{
              display: 'flex',
              gap: '1rem',
              justifyContent: 'center',
              position: 'relative',
              zIndex: 1
            }}>
              <button
                onClick={cancelDeleteReceptionist}
                disabled={isSubmitting}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '12px',
                  border: '2px solid #E5E7EB',
                  background: '#FFFFFF',
                  color: '#6B7280',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.background = '#F9FAFB';
                    e.target.style.borderColor = '#D1D5DB';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.background = '#FFFFFF';
                  e.target.style.borderColor = '#E5E7EB';
                }}
              >
                Cancel
              </button>
              <button
                onClick={confirmDeleteReceptionist}
                disabled={isSubmitting}
                style={{
                  padding: '0.75rem 2rem',
                  borderRadius: '12px',
                  border: 'none',
                  background: 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)',
                  color: '#FFFFFF',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: isSubmitting ? 'not-allowed' : 'pointer',
                  transition: 'all 0.3s ease',
                  opacity: isSubmitting ? 0.5 : 1,
                  boxShadow: '0 4px 12px rgba(239, 68, 68, 0.4)'
                }}
                onMouseEnter={(e) => {
                  if (!isSubmitting) {
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 16px rgba(239, 68, 68, 0.5)';
                  }
                }}
                onMouseLeave={(e) => {
                  e.target.style.transform = 'translateY(0)';
                  e.target.style.boxShadow = '0 4px 12px rgba(239, 68, 68, 0.4)';
                }}
              >
                {isSubmitting ? 'Deleting...' : 'Delete Permanently'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Success Popup */}
      {showDeleteSuccessPopup && (
        <div 
          className="popup-overlay" 
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10000,
            animation: 'fadeIn 0.3s ease-in'
          }}
          onClick={() => setShowDeleteSuccessPopup(false)}
        >
          <div 
            style={{
              background: '#FFFFFF',
              borderRadius: '20px',
              padding: '3rem',
              maxWidth: '500px',
              width: '90%',
              textAlign: 'center',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
              animation: 'slideUp 0.4s ease-out',
              position: 'relative',
              overflow: 'hidden'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            {/* Decorative circles */}
            <div style={{
              position: 'absolute',
              top: '-50px',
              right: '-50px',
              width: '150px',
              height: '150px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>
            <div style={{
              position: 'absolute',
              bottom: '-30px',
              left: '-30px',
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)',
              zIndex: 0
            }}></div>

            {/* Success Icon */}
            <div style={{
              width: '100px',
              height: '100px',
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 1.5rem',
              boxShadow: '0 10px 25px rgba(16, 185, 129, 0.4)',
              position: 'relative',
              zIndex: 1,
              animation: 'scaleIn 0.5s ease-out'
            }}>
              <span style={{ 
                fontSize: '4rem', 
                color: '#FFFFFF',
                fontWeight: 'bold',
                lineHeight: 1
              }}>✓</span>
            </div>

            {/* Title */}
            <h2 style={{
              color: '#1F2937',
              fontSize: '2rem',
              fontWeight: '700',
              marginBottom: '0.5rem',
              position: 'relative',
              zIndex: 1
            }}>
              Receptionist Deleted Successfully! 🎉
            </h2>

            {/* Message */}
            <p style={{
              color: '#6B7280',
              fontSize: '1rem',
              marginBottom: '2rem',
              lineHeight: '1.6',
              position: 'relative',
              zIndex: 1
            }}>
              <strong>{deletedReceptionistName}</strong> has been permanently deleted from the database.
            </p>

            {/* Button */}
            <button
              onClick={() => setShowDeleteSuccessPopup(false)}
              style={{
                padding: '0.75rem 2rem',
                borderRadius: '12px',
                border: 'none',
                background: 'linear-gradient(135deg, #10B981 0%, #059669 100%)',
                color: '#FFFFFF',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer',
                transition: 'all 0.3s ease',
                boxShadow: '0 4px 12px rgba(16, 185, 129, 0.4)'
              }}
              onMouseEnter={(e) => {
                e.target.style.transform = 'translateY(-2px)';
                e.target.style.boxShadow = '0 6px 16px rgba(16, 185, 129, 0.5)';
              }}
              onMouseLeave={(e) => {
                e.target.style.transform = 'translateY(0)';
                e.target.style.boxShadow = '0 4px 12px rgba(16, 185, 129, 0.4)';
              }}
            >
              OK
            </button>
          </div>
        </div>
      )}

      {/* CSS Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes slideUp {
          from { transform: translateY(30px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        @keyframes scaleIn {
          from { transform: scale(0); opacity: 0; }
          to { transform: scale(1); opacity: 1; }
        }
      `}</style>
    </div>
  );
};

export default Receptionists;

